import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getWhatsAppMessages, sendWhatsAppMessage, markWhatsAppAsRead, type WhatsAppMessage } from '../services/whatsapp';
import { WhatsAppIcon, RefreshIcon } from './icons/AdminIcons';
import './WhatsAppSection.css';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

interface HistorialWhatsAppTabProps {
  userId: number;
  whatsapp: string | null;
  onSaveWhatsapp: (phone: string | null) => void;
  getApiUrl: () => string;
  isDarkMode?: boolean;
}

const HistorialWhatsAppTab: React.FC<HistorialWhatsAppTabProps> = ({
  userId,
  whatsapp,
  onSaveWhatsapp,
  getApiUrl,
  isDarkMode,
}) => {
  const [linkPhone, setLinkPhone] = useState(whatsapp || '');
  const [saving, setSaving] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async (phone: string) => {
    if (!phone.trim()) return;
    setLoadingMessages(true);
    setMsgError(null);
    try {
      const data = await getWhatsAppMessages(phone.trim());
      setMessages(data.messages || []);
      await markWhatsAppAsRead(phone.trim());
    } catch (e) {
      setMsgError(e instanceof Error ? e.message : 'Error al cargar mensajes');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (whatsapp?.trim()) loadMessages(whatsapp.trim());
    else setMessages([]);
  }, [whatsapp, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSaveLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = linkPhone.trim();
    setLinkError(null);
    setSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ whatsapp: phone || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar');
      const value = data.whatsapp ?? (phone || null);
      onSaveWhatsapp(value);
      setLinkPhone(value || '');
      if (value) loadMessages(value);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whatsapp?.trim() || !replyText.trim()) return;
    setSending(true);
    setMsgError(null);
    try {
      await sendWhatsAppMessage(whatsapp.trim(), replyText.trim());
      setReplyText('');
      loadMessages(whatsapp.trim());
    } catch (e) {
      setMsgError(e instanceof Error ? e.message : 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  if (!whatsapp?.trim()) {
    return (
      <div className="admin-crm-empty-state" style={{ padding: '16px' }}>
        <p style={{ marginBottom: '12px' }}>Vincular este contacto a un número de WhatsApp para ver el chat aquí.</p>
        <form onSubmit={handleSaveLink} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '320px' }}>
          <input
            type="text"
            value={linkPhone}
            onChange={(e) => setLinkPhone(e.target.value)}
            placeholder="Ej: +54 9 11 1234-5678"
            className="admin-filter-select"
            style={{ width: '100%' }}
          />
          {linkError && <p style={{ color: '#ef4444', fontSize: '14px' }}>{linkError}</p>}
          <button type="submit" className="admin-action-button-small" disabled={saving}>
            {saving ? 'Guardando...' : 'Vincular número'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className={`wa-section ${isDarkMode ? 'wa-section-dark' : ''}`} style={{ maxHeight: '400px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <WhatsAppIcon width={18} height={18} />
          {whatsapp}
        </span>
        <button type="button" className="gmail-btn gmail-btn-icon" onClick={() => loadMessages(whatsapp)} title="Actualizar" disabled={loadingMessages}>
          <RefreshIcon width={16} height={16} />
        </button>
      </div>
      <div className="wa-messages-container" style={{ flex: 1, overflow: 'auto', minHeight: '200px' }}>
        {loadingMessages ? (
          <div className="wa-loading-dots">Cargando...</div>
        ) : messages.length === 0 ? (
          <div style={{ padding: '16px', color: '#6b7280' }}>No hay mensajes con este número.</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`wa-message-bubble ${m.direction === 'outbound' ? 'outbound' : 'inbound'}`}>
              <div className="wa-message-body">{m.body}</div>
              <div className="wa-message-time">{formatDate(m.createdAt)}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      {msgError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{msgError}</p>}
      <form onSubmit={handleSendReply} className="wa-reply-form" style={{ marginTop: '8px' }}>
        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Escribe un mensaje..."
          rows={2}
          disabled={sending}
        />
        <button type="submit" className="wa-btn wa-btn-primary" disabled={sending || !replyText.trim()}>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </form>
    </div>
  );
};

export default HistorialWhatsAppTab;
